import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import SeeItInActionCarousel from "../components/SeeItInActionCarousel";
import TestimonialsSection from "../components/TestimonialsSection";
import {
    Gavel, Image as ImageIcon, Lightning, ChartLineUp, Shield, Users, UsersThree, Ticket,
    ArrowRight, CheckCircle, Sparkle, CreditCard, FilmStrip, HandHeart, Confetti, Flag, Heart,
    QrCode, GameController, Package, ChatCircle, Repeat, Tag, Envelope, BookOpen, Bank,
    ChartLine, Code, ArrowsClockwise, CalendarBlank, FileText, AddressBook, Storefront,
    DeviceMobile, Coins, Crown, Megaphone, Robot, Globe, Receipt, TrendUp
} from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1768508948835-7dbab7ca6d58?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxjaGFyaXR5JTIwZ2FsYSUyMGV2ZW50JTIwZGlubmVyfGVufDB8fHx8MTc3OTQ1NjA1OXww&ixlib=rb-4.1.0&q=85";
const ART_IMG = "https://images.unsplash.com/photo-1773304292149-1d721aa70346?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxmaW5lJTIwYXJ0JTIwcGFpbnRpbmclMjBnYWxsZXJ5fGVufDB8fHx8MTc3OTQ1NjA1OXww&ixlib=rb-4.1.0&q=85";
const WATCH_IMG = "https://images.unsplash.com/photo-1524805444758-089113d48a6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB3YXRjaCUyMHZpbnRhZ2V8ZW58MHx8fHwxNzc5NDU2MDU5fDA&ixlib=rb-4.1.0&q=85";
const GALA_IMG = "https://images.unsplash.com/photo-1768508950324-ee6342209840?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxjaGFyaXR5JTIwZ2FsYSUyMGV2ZW50JTIwZGlubmVyfGVufDB8fHx8MTc3OTQ1NjA1OXww&ixlib=rb-4.1.0&q=85";

const stagger = { animate: { transition: { staggerChildren: 0.06 } } };
const fadeUp = {
    initial: { opacity: 0, y: 24 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

// "Make it rain" — falling money for the Burning Money hype section
const MONEY_EMOJIS = ["💵", "💸", "💰", "🤑", "💴", "💶", "💷"];
const MONEY_RAIN = Array.from({ length: 30 }).map((_, i) => ({
    id: i,
    emoji: MONEY_EMOJIS[i % MONEY_EMOJIS.length],
    left: Math.round(Math.random() * 98),
    size: (1.3 + Math.random() * 2.0).toFixed(2),
    dur: (5 + Math.random() * 6).toFixed(2),
    delay: (Math.random() * 7).toFixed(2),
}));

const FUNDRAISING_FEATURES = [
    { icon: Gavel, title: "Live & silent auctions", desc: "Timed bidding, mobile paddles, reserve protection, anti-snipe auto-extend, drag-drop photos & video." },
    { icon: HandHeart, title: "Donations & giving pages", desc: "Branded forms with thermometer, impact tiers, suggested amounts, donor tipping, anonymous gifts." },
    { icon: Repeat, title: "Recurring donations", desc: "Monthly / quarterly / yearly Stripe subscriptions with auto-renewal and MRR dashboard." },
    { icon: UsersThree, title: "Peer-to-peer fundraising", desc: "Your supporters become fundraisers. Personal pages, team leaderboards, donate-to-vote mode." },
    { icon: Coins, title: "Grant sourcing & AI writing", desc: "A free, curated grant directory with live Grants.gov import, plus an AI Grant Writer that drafts full, on-brand proposals — find funding and write winning applications without leaving the platform." },
    { icon: Ticket, title: "Events & ticketing", desc: "Tickets, sponsor packages, a custom registration form builder (meal choice, dietary, per-guest names), min/max purchase limits, .ics + Google Calendar sync, promo codes." },
    { icon: Confetti, title: "Raffles", desc: "Compliant ticket sales with caps, random winner draws, confetti celebrations and livestream embeds." },
    { icon: Flag, title: "Golf tournaments", desc: "Foursomes, hole sponsors, 18-hole scorecards, live leaderboard — the full club-house workflow." },
    { icon: Heart, title: "Fund-a-Need / Paddle raise", desc: "Tiered live appeal with public progress bar and a real-time paddle ticker your MC will love." },
    { icon: GameController, title: "Reverse raffle & Heads-or-Tails", desc: "Crowd games that drive ticket sales and party energy — admin-driven, mobile-friendly." },
    { icon: Storefront, title: "Fixed-price store / Buy-it-Now", desc: "Sell merchandise, swag, or limited-quantity items with quantity tracking and instant checkout." },
    { icon: QrCode, title: "Event check-in", desc: "QR rosters, in/out actions, kiosk mode for big-screen progress at the door." },
    { icon: ChatCircle, title: "Per-table QR chat", desc: "Guests scan their table's QR to message staff live or tap quick requests (drinks, auctioneer, check-out). Staff reply + broadcast 'dinner is served' — unique to us." },
    { icon: Package, title: "Fulfillment workflow", desc: "Ready → shipped → picked-up → delivered with tracking and audit trail." },
];

const REACH_FEATURES = [
    { icon: Envelope, title: "Email campaigns", desc: "Dual-provider email infrastructure with automatic failover. Audience by tier, tag, donor, attendee, sponsor, fundraiser." },
    { icon: ChatCircle, title: "SMS & Text-to-Give", desc: "Carrier-direct SMS. Bulk segments, keyword-driven giving (TEXT GIVE 50), opt-out compliance." },
    { icon: BookOpen, title: "Template library", desc: "Polished, opinionated templates for receipts, year-end appeals, gala invites, monthly-donor welcome." },
    { icon: Megaphone, title: "Outbid notifications", desc: "Lose a bid? We email and SMS the previous high bidder instantly — and they come back." },
    { icon: Globe, title: "Embeddable donate widget", desc: "Drop a single &lt;script&gt; tag on any website. Branded color & label, fully responsive." },
    { icon: ChartLineUp, title: "Live public scoreboard", desc: "Project a real-time fundraising dashboard at your gala. Total raised, top donors, live activity." },
    { icon: DeviceMobile, title: "Kiosk mode", desc: "Big-screen, auto-refreshing read-only view for lobbies, donor walls, and check-in tables." },
    { icon: TrendUp, title: "Live thermometer & milestones", desc: "Animated goal bars and toast pops at 25 / 50 / 75 / 100% — the energy multiplier." },
];

const DONOR_FEATURES = [
    { icon: Robot, title: "Donor Intelligence Hub (AI)", desc: "iWave-style wealth & affinity screening built in. AI scores every donor's giving capacity and affinity, drafts a cultivation brief, and screens an entire event guest list in one pass." },
    { icon: TrendUp, title: "Capacity & affinity scoring", desc: "Every donor gets a computed capacity band and an affinity score, so gift officers work the right 30 conversations instead of all 3,000 contacts." },
    { icon: Crown, title: "Moves management & portfolios", desc: "Cultivation stages, a follow-up task board, and per-officer 'My Portfolio' dashboards. Assign donors to a staff owner; nothing goes cold." },
    { icon: Users, title: "Householding & soft-credits", desc: "Link spouses and family into one household and soft-credit the influencer behind a gift — flows straight into reports and year-end statements." },
    { icon: Coins, title: "Offline & legacy gifts", desc: "Log checks, wires, stock, DAF, and pledge payments. They live on the same donor profiles and tax statements as online gifts." },
    { icon: AddressBook, title: "Donor CRM & tier classification", desc: "Auto-classify every donor as Major / Mid / Grassroots / Lapsed / New / Prospect based on lifetime + recency." },
    { icon: ImageIcon, title: "CSV import & bulk assignment", desc: "Bring legacy donor lists in 30 seconds with smart column mapping. Reassign hundreds of donors to a new gift officer in one action." },
    { icon: Users, title: "Unified donor profiles", desc: "Bids, wins, donations, tickets, sponsorships, P2P, notes, tasks — every interaction on one timeline per person." },
    { icon: Lightning, title: "Workflow automations", desc: "Drip emails on triggers: donation paid, days after gift, anniversary, lapsed, ticket purchased." },
];

const FINANCE_FEATURES = [
    { icon: CreditCard, title: "Cards, Apple Pay, Google Pay", desc: "Stripe Checkout in dozens of countries. Apple/Google Pay, ACH, Klarna, Afterpay — all on by default." },
    { icon: Coins, title: "PayPal & Venmo", desc: "Native PayPal + Venmo with Pay-Later. Same tipping, fees, and persistence as cards." },
    { icon: Bank, title: "Stripe Connect (direct payout)", desc: "Funds land in YOUR bank account on a 2-day rolling payout. Or stay on platform-mode and we forward." },
    { icon: CheckCircle, title: "Auto-bill auction winners", desc: "Auction closes → winners charged automatically. Smart retry on declines, no chase, no missed wins." },
    { icon: Receipt, title: "Automated year-end tax statements", desc: "Consolidated IRS-compliant PDFs per donor — including soft-credits — with your letterhead, EIN, and itemized table. A scheduler auto-emails the whole campaign every late January." },
    { icon: ChartLine, title: "Reports & exports", desc: "KPIs, 30-day trends, top donors, CSV per module, platform-wide reconciliation across providers." },
    { icon: Shield, title: "Reserve protection", desc: "Set reserves on premium items. Items below reserve don't sell. You always stay in control." },
    { icon: Tag, title: "Promo codes", desc: "% or $ off, optional event scoping, max-uses cap. Cross-org-leak-proof." },
];

const INTEGRATION_FEATURES = [
    { icon: Robot, title: "AI everywhere", desc: "Item descriptions, campaign copy, thank-yous, smart starting bids, segment suggestions, Fund-a-Need narratives, SMS drafts." },
    { icon: ArrowsClockwise, title: "Outbound webhooks (Zapier-killer)", desc: "15 event types. HMAC-signed. Pipe data into Slack, Notion, Google Sheets, your CRM — anywhere." },
    { icon: Bank, title: "Accounting sync (QuickBooks + more)", desc: "Native QuickBooks Online OAuth with idempotent Sales Receipt sync, plus universal exports that work with every commonly used accounting platform — Xero, Sage Intacct, Blackbaud Financial Edge, NetSuite and more." },
    { icon: CalendarBlank, title: "Google Calendar", desc: "Two-way event sync. Public 'Add to Google Calendar' link on every event page." },
    { icon: FileText, title: "Printable bid sheets", desc: "One-click PDFs with photo, QR code, pricing strip — printed silent-auction sheets without leaving the platform." },
    { icon: Code, title: "Multi-tenant + custom branding", desc: "Each org gets their own storefront, microsite, brand colors, logo, and custom domain (coming soon)." },
];

export default function LandingPage() {
    return (
        <div className="bg-background text-foreground min-h-screen" data-testid="landing-page">
            <SEOHead
                title="CharityFundraiser — All-in-One Fundraising Platform for Nonprofits"
                description="One login. One website. One dashboard — no more confusion. Silent + live auctions, donations, peer-to-peer, ticketing, sponsorships, raffles, golf tournaments, donor CRM, and AI campaign coaching for 501(c)(3) organizations. Flat 2% — no donor tip nag."
                keywords={[
                    "charity fundraiser software",
                    "all-in-one nonprofit fundraising platform",
                    "online silent auction software",
                    "mobile bidding for fundraisers",
                    "text-to-give platform",
                    "peer-to-peer fundraising software",
                    "donor CRM for nonprofits",
                    "givebutter alternative",
                    "onecause alternative",
                    "AI fundraising assistant",
                    "best silent auction software for schools",
                    "fundraising software with stripe connect",
                ]}
                canonical="/"
                type="website"
            />
            <Navbar variant="marketing" />

            {/* HERO */}
            <section className="relative min-h-[100vh] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 hero-image"
                    style={{ backgroundImage: `url(${HERO_IMG})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-primary/20" />

                <div className="relative z-10 px-6 md:px-12 lg:px-24 pb-24 pt-40 w-full">
                    <motion.div initial="initial" animate="animate" variants={stagger} className="max-w-5xl">
                        <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8 drop-shadow-[0_2px_8px_rgba(0,0,0,0.75)]">
                            <span className="live-dot" />
                            <span className="text-sm md:text-base font-bold uppercase tracking-[0.25em] text-white">
                                One Login · One Website · One Dashboard
                                <span className="text-accent normal-case tracking-normal font-light italic ml-2">— no more confusion.</span>
                            </span>
                        </motion.div>

                        <motion.h1
                            variants={fadeUp}
                            className="text-white font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[1.05] tracking-tight max-w-5xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.55)]"
                        >
                            Every way your non-profit raises money. <em className="text-accent not-italic font-normal">All under one roof.</em>
                        </motion.h1>

                        <motion.p variants={fadeUp} className="text-white text-xl md:text-2xl font-light max-w-3xl mt-10 leading-relaxed drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
                            Every way to raise — auctions, donations, recurring giving, peer-to-peer, ticketed events, raffles, golf tournaments, grant sourcing & AI grant writing and more — plus an AI donor-intelligence CRM, email + SMS, and live scoreboards. One login. One dashboard. One team behind you.
                        </motion.p>

                        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 mt-12">
                            <Link to="/signup-org" className="btn-accent" data-testid="hero-cta-primary">
                                Start fundraising free <ArrowRight size={16} weight="bold" />
                            </Link>
                            <Link to="/login" className="btn-outline border-white/60 text-white hover:bg-white/10 hover:border-white" data-testid="hero-cta-secondary">
                                Sign in
                            </Link>
                        </motion.div>

                        <motion.div variants={fadeUp} className="mt-6">
                            <Link to="/compare" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/85 hover:text-accent transition-colors border-b border-white/30 hover:border-accent pb-1" data-testid="hero-compare-link">
                                <span className="opacity-80">Coming from</span>
                                <span className="font-bold">Givebutter</span>
                                <span className="opacity-60">·</span>
                                <span className="font-bold">OneCause</span>
                                <span className="opacity-60">·</span>
                                <span className="font-bold">DonorPerfect</span>
                                <span className="opacity-80">?</span>
                                <span>See the full comparison</span>
                                <ArrowRight size={11} weight="bold" />
                            </Link>
                        </motion.div>

                        <motion.p variants={fadeUp} className="mt-6 text-sm text-white/90 max-w-2xl flex items-start gap-2 drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" data-testid="hero-volunteer-callout">
                            <span className="text-accent text-base leading-none">✦</span>
                            <span><span className="font-semibold text-accent">New —</span> Know exactly which volunteer scanned 80% of your guests and rung up the most POS sales. Real per-volunteer efficiency reports, stamped audit trail, 4-digit kiosk PINs. Nobody else does this.</span>
                        </motion.p>

                        <motion.div variants={fadeUp} className="mt-8">
                            <Link
                                to="/demo"
                                className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] hover:shadow-lg transition-shadow"
                                data-testid="hero-demo-cta"
                            >
                                <Sparkle size={14} weight="fill" /> Try a live sandbox in 10 seconds
                                <ArrowRight size={14} weight="bold" />
                            </Link>
                            <p className="text-[11px] text-white/65 mt-2">No sign-up · expires after 4 hours · stocked with real demo data</p>
                        </motion.div>

                        <motion.div variants={fadeUp} className="grid grid-cols-3 gap-8 mt-20 max-w-3xl border-t border-white/30 pt-10">
                            <div>
                                <div className="data-num text-3xl text-white font-medium">$0</div>
                                <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/85 mt-2">Setup fee</div>
                            </div>
                            <div>
                                <div className="data-num text-3xl text-white font-medium">$0</div>
                                <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/85 mt-2">Monthly</div>
                            </div>
                            <div>
                                <div className="data-num text-3xl text-white font-medium">∞</div>
                                <div className="text-xs font-bold uppercase tracking-[0.18em] text-white/85 mt-2">Modules included</div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* BURNING MONEY — make-it-rain hype section (Rick Stacy homage) */}
            <section className="burn-section py-20 md:py-28 px-6 md:px-12 lg:px-24" data-testid="burning-money-section">
                <div className="money-rain" aria-hidden="true">
                    {MONEY_RAIN.map((m) => (
                        <span
                            key={m.id}
                            className="money-bill"
                            style={{
                                left: `${m.left}%`,
                                fontSize: `${m.size}rem`,
                                animationDuration: `${m.dur}s`,
                                animationDelay: `${m.delay}s`,
                            }}
                        >
                            {m.emoji}
                        </span>
                    ))}
                </div>

                <div className="relative z-10 max-w-5xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.6 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="burning-cash text-7xl md:text-8xl mb-6 inline-block"
                        data-testid="burning-cash-emoji"
                    >
                        <span className="flames text-6xl md:text-7xl" aria-hidden="true">🔥</span>
                        <span>💸</span>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-accent font-bold uppercase tracking-[0.3em] text-xs md:text-sm mb-5"
                    >
                        Let's get real for a second
                    </motion.p>

                    <motion.h2
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="burn-heading font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight"
                        data-testid="burning-money-headline"
                    >
                        If your non-profit isn't using<br className="hidden sm:block" /> Charityfundraiser.com,<br />
                        you're just <span className="whitespace-nowrap">BURNING MONEY! 🔥</span>
                    </motion.h2>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="text-white/85 text-lg md:text-xl font-light max-w-2xl mx-auto mt-8 leading-relaxed"
                    >
                        Every bloated platform fee, every clunky tool, every donation you can't capture —
                        that's your hard-raised cash going up in smoke. <span className="font-semibold text-accent">$0 setup. $0 monthly. The lowest fee in the industry.</span>
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
                    >
                        <Link
                            to="/signup-org"
                            className="burn-cta inline-flex items-center gap-2 px-8 py-4 text-sm md:text-base font-bold uppercase tracking-[0.15em] rounded-sm transition-transform"
                            data-testid="burning-money-cta"
                        >
                            Stop burning money — start free <ArrowRight size={18} weight="bold" />
                        </Link>
                        <span className="text-white/55 text-xs uppercase tracking-[0.18em]">No card · No contract · No fees to start</span>
                    </motion.div>
                </div>
            </section>

            {/* PROMISE BAR */}
            <section className="bg-accent text-accent-foreground py-6 px-6 md:px-12 lg:px-24" data-testid="promise-bar">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
                    <p className="font-heading text-2xl md:text-3xl leading-tight">
                        We get paid only when <em>you</em> get paid. <span className="opacity-80">— and only the smallest fraction in the industry.</span>
                    </p>
                    <Link to="/signup-org" className="bg-primary text-primary-foreground px-6 py-3 text-xs uppercase font-bold tracking-[0.2em] hover:bg-primary/90 transition-colors shrink-0 inline-flex items-center gap-2" data-testid="promise-cta">
                        See pricing <ArrowRight size={14} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* AUCTIONEER DIRECTORY — exclusive benefit (lifted to top of marketing funnel) */}
            <section className="bg-primary text-primary-foreground py-16 md:py-20 px-6 md:px-12 lg:px-24 border-y-2 border-accent/40" data-testid="auctioneer-directory-callout">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-7">
                            <p className="label-eyebrow text-accent flex items-center gap-2">
                                <span className="inline-block w-2 h-2 bg-accent rounded-full animate-pulse"></span>
                                Only on CharityFundraiser
                            </p>
                            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mt-3 leading-tight">
                                The largest benefit-auctioneer directory <em className="text-accent">in the country</em>, baked right into your platform.
                            </h2>
                            <p className="text-base text-white/85 mt-5 max-w-2xl leading-relaxed">
                                Givebutter, OneCause, BiddingForGood, DonorPerfect — <strong className="text-white">none of them</strong> help you find an auctioneer. We do.
                                <span className="block mt-3">Search <span className="data-num text-accent">8,800+</span> vetted benefit auctioneers across all 50 states. Filter by state, city, or specialty (gala, silent, paddle raise, fund-a-need). Free. No sign-up. No middleman fees. Built directly into your event-creation flow.</span>
                            </p>
                            <div className="flex flex-wrap gap-4 mt-7">
                                <Link to="/find-an-auctioneer" onClick={() => { try { window.gtag && window.gtag("event", "select_promotion", { promotion_name: "auctioneer_directory_hero", creative_slot: "landing_hero_callout" }); } catch {} }} className="bg-accent text-accent-foreground px-7 py-3.5 text-xs uppercase font-bold tracking-[0.2em] hover:bg-accent/90 transition-colors inline-flex items-center gap-2" data-testid="landing-find-auctioneer-cta">
                                    Browse the directory <ArrowRight size={14} weight="bold" />
                                </Link>
                                <Link to="/find-an-auctioneer/join" className="bg-transparent text-white border border-white/40 px-7 py-3.5 text-xs uppercase font-bold tracking-[0.2em] hover:bg-white/10 transition-colors inline-flex items-center gap-2" data-testid="landing-auctioneer-join-cta">
                                    I'm an auctioneer
                                </Link>
                            </div>
                        </div>
                        <div className="md:col-span-5">
                            <div className="grid grid-cols-2 gap-px bg-accent/20" data-testid="directory-stats">
                                <div className="bg-primary p-5 md:p-6">
                                    <p className="data-num text-3xl md:text-4xl text-accent">8,800+</p>
                                    <p className="text-xs uppercase tracking-wider text-white/65 mt-2">Auctioneers nationwide</p>
                                </div>
                                <div className="bg-primary p-5 md:p-6">
                                    <p className="data-num text-3xl md:text-4xl text-accent">50</p>
                                    <p className="text-xs uppercase tracking-wider text-white/65 mt-2">States covered</p>
                                </div>
                                <div className="bg-primary p-5 md:p-6">
                                    <p className="data-num text-3xl md:text-4xl text-accent">$0</p>
                                    <p className="text-xs uppercase tracking-wider text-white/65 mt-2">To search & contact</p>
                                </div>
                                <div className="bg-primary p-5 md:p-6">
                                    <p className="data-num text-3xl md:text-4xl text-accent">1-click</p>
                                    <p className="text-xs uppercase tracking-wider text-white/65 mt-2">Match from event page</p>
                                </div>
                            </div>
                            <p className="text-xs text-white/55 mt-3 italic leading-relaxed">
                                Auctioneers can claim their listing free or buy a Featured slot ($99/yr, one per city) for top placement.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* SCALE STRIP */}
            <section className="bg-background border-y border-border py-12 md:py-16 px-6 md:px-12 lg:px-24" data-testid="scale-strip">
                <div className="max-w-7xl mx-auto">
                    <p className="label-eyebrow text-accent text-center mb-10">Built for events of every size</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
                        {[
                            { value: "5 → 5,000", label: "Guests per event", sub: "Same flow at intimate dinners or stadium galas" },
                            { value: "1 → 50+", label: "Events per year", sub: "One platform, every event the org runs" },
                            { value: "∞", label: "Properties / chapters", sub: "Multi-tenant from day one" },
                            { value: "$0 → $5M+", label: "Per-event volume", sub: "Same fees. Same speed. No \"enterprise\" gates." },
                        ].map((s, i) => (
                            <div key={i} className="bg-card p-6 md:p-8 flex flex-col" data-testid={`scale-${i}`}>
                                <span className="font-heading text-3xl md:text-4xl text-primary data-num leading-none">{s.value}</span>
                                <span className="label-eyebrow text-[10px] text-accent mt-4">{s.label}</span>
                                <span className="text-xs text-foreground/65 mt-2 leading-relaxed">{s.sub}</span>
                            </div>
                        ))}
                    </div>
                    <p className="text-center text-foreground/60 text-sm mt-8 max-w-2xl mx-auto">
                        From a 25-guest backyard wine tasting to a 4,800-guest convention-center black-tie — same QR-tag flow, same dashboard, same fees.
                    </p>
                </div>
            </section>

            {/* (auctioneer callout moved above — old position removed to avoid duplication) */}

            {/* SEE IT IN ACTION TEASER */}
            <SeeItInActionCarousel />

            {/* SOCIAL PROOF */}
            <TestimonialsSection />

            {/* FUNDRAISING TOOLS */}
            <FeatureSection
                eyebrow="Fundraising tools"
                title={<>Every way to raise. <em className="text-accent">All in one platform.</em></>}
                lead="Whatever your format — silent auction, gala, golf outing, 5K, paddle raise, online appeal, or grant funding — we've already built it. No add-ons. No upsells. No 'available in our Pro tier.'"
                items={FUNDRAISING_FEATURES}
                testIdPrefix="fundraising"
            />

            {/* DONOR / CRM */}
            <FeatureSection
                eyebrow="Enterprise CRM & donor intelligence"
                title={<>Know your donors. <em className="text-accent">Then move them.</em></>}
                lead="More than a donor list — a full major-gift CRM. AI scores each donor's capacity and affinity, moves management tracks every cultivation stage, and gift officers get their own portfolios. The donor intelligence nonprofits buy iWave and Bloomerang for, included free."
                items={DONOR_FEATURES}
                testIdPrefix="donor"
                inverted
            />

            {/* REACH */}
            <FeatureSection
                eyebrow="Outreach"
                title={<>Email, SMS, and on-screen energy <em className="text-accent">that converts.</em></>}
                lead="Reach donors where they are — in their inbox, on their phone, on the big screen at your gala. All channels share one audience, one brand, one set of merge tags."
                items={REACH_FEATURES}
                testIdPrefix="reach"
            />

            {/* FINANCE */}
            <FeatureSection
                eyebrow="Money flow"
                title={<>Every payment method donors want. <em className="text-accent">Money in your bank.</em></>}
                lead="Stripe Checkout LIVE, PayPal & Venmo native, Apple Pay, Google Pay, ACH, Klarna, Afterpay. Funds land in YOUR Stripe Connect account on a rolling payout — not held hostage."
                items={FINANCE_FEATURES}
                testIdPrefix="finance"
                inverted
            />

            {/* FRICTIONLESS EVENTS */}
            <section id="features" className="scroll-mt-20 py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground" data-testid="frictionless-section">
                <div className="grid md:grid-cols-12 gap-16 items-start">
                    <div className="md:col-span-5">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">No-line events</p>
                        <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-[1.1]">
                            Move 400 guests <em className="text-accent">in 9 minutes</em>. Move them out without a single unpaid tab.
                        </h2>
                        <p className="text-white/85 mt-6 text-lg leading-relaxed">
                            Most galas lose 8–14% of their auction revenue to <em>unpaid bills</em> — guests who left without checking out. They didn't mean to. They just got distracted. We close that hole.
                        </p>
                        <p className="text-white/70 text-sm mt-4 leading-relaxed">
                            One QR tag per guest. Scan to admit at the door, scan to ring up mulligans / raffle / silent-auction wins, scan to settle on the way out. Volunteers don't need an app — they use the phone in their pocket.
                        </p>
                        <p className="text-white/70 text-sm mt-3 leading-relaxed">
                            Plus a <strong className="text-accent">seating-chart lobby kiosk</strong>, <strong className="text-accent">3-way QR check-in</strong> (prepaid email, walk-up DIY self-register, paddle scan), <strong className="text-accent">11 payment methods</strong> for donations, and <strong className="text-accent">card-on-file</strong> so end-of-night settlement is a single auto-charge instead of a chase.
                        </p>
                        <Link to="/features" className="mt-6 inline-flex items-center gap-2 text-accent hover:text-amber-400 text-sm uppercase tracking-wider font-bold border-b border-accent/40 hover:border-amber-400 pb-1" data-testid="landing-features-link">
                            Browse the full feature inventory →
                        </Link>
                    </div>

                    <div className="md:col-span-7 space-y-px bg-white/5">
                        {[
                            {
                                n: "Door",
                                t: "2-second admission",
                                d: "Staff scans a guest's QR with their phone camera. Big green \"Admit Jane\" card. One tap. Done. Duplicate scans trigger a red warning so nobody slips in on a stolen tag.",
                                metric: "−83% door-line wait",
                            },
                            {
                                n: "Floor",
                                t: "One tab, every spend",
                                d: "Mulligans, raffle tickets, side games, silent-auction wins, paddle raises — anything a volunteer rings up lands on the guest's single running tab. Per-item caps prevent overselling (no one buys 47 mulligans).",
                                metric: "1 bill, not 7 receipts",
                            },
                            {
                                n: "Exit",
                                t: "Smart \"heading out?\" alert",
                                d: "When a guest has been at the event 2+ hours AND has unsettled charges, we auto-text + email them the one-tap settlement link. No more chasing donors on Monday morning.",
                                metric: "+11% net collected",
                            },
                            {
                                n: "Live",
                                t: "Attendance counter you can project",
                                d: "Big-number live attendance display: admitted, registered, expected, walk-ins. Refreshes every 8 seconds. Boards love it. Sponsors love it. Caterers love it.",
                                metric: "Real-time, all night",
                            },
                            {
                                n: "Always",
                                t: "Guests can't lose their tag",
                                d: "QR is emailed and MMS'd to them the moment a paddle # is assigned. Lose the printed tag? Pull it up on their phone in 2 seconds. Need a re-print? One-label-on-a-half-used-Avery-sheet reprint built in.",
                                metric: "Zero re-prints needed",
                            },
                        ].map((s, i) => (
                            <div key={i} className="bg-primary p-6 md:p-7 grid grid-cols-12 gap-4 items-baseline" data-testid={`frictionless-${i}`}>
                                <span className="col-span-2 md:col-span-1 label-eyebrow text-accent text-[10px]">{s.n}</span>
                                <div className="col-span-10 md:col-span-8">
                                    <h3 className="font-heading text-xl md:text-2xl leading-tight">{s.t}</h3>
                                    <p className="text-white/75 mt-2 leading-relaxed text-sm md:text-base">{s.d}</p>
                                </div>
                                <span className="col-span-12 md:col-span-3 data-num text-accent text-sm md:text-right font-bold uppercase tracking-wider" style={{ letterSpacing: "0.04em" }}>{s.metric}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="border-t border-white/20 mt-16 pt-10 text-center max-w-3xl mx-auto">
                    <p className="font-heading text-xl md:text-2xl text-white/90 leading-relaxed">
                        A 400-person gala costs roughly <em className="text-accent">$2,800</em> in volunteer time at the door + <em className="text-accent">8–14%</em> of auction revenue in unpaid tabs. <span className="block mt-4">Our QR-tag flow eliminates both — built in, no add-on fees.</span>
                    </p>
                </div>
            </section>

            {/* INTEGRATIONS / AI */}
            <FeatureSection
                eyebrow="Built-in superpowers"
                title={<>AI, accounting, and integrations <em className="text-accent">that just work.</em></>}
                lead="AI ghost-writes your catalog copy. Outbound webhooks pipe data anywhere. Native QuickBooks sync plus universal exports keep any accounting software in step, and Google Calendar syncs automatically. Everything is multi-tenant, branded, and built to scale."
                items={INTEGRATION_FEATURES}
                testIdPrefix="integrations"
            />

            {/* HOW IT WORKS */}
            <section id="how" className="scroll-mt-20 py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground">
                <div className="grid md:grid-cols-12 gap-16">
                    <div className="md:col-span-5">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">How it works</p>
                        <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-tight">
                            From signup to settled in <em className="text-accent">a single afternoon.</em>
                        </h2>
                        <p className="text-white/85 mt-6 text-lg leading-relaxed">
                            No procurement specialists. No IT department. No six-week onboarding. Just a guided flow your team will actually finish.
                        </p>
                    </div>
                    <div className="md:col-span-7 space-y-8">
                        {[
                            { n: "01", t: "Create your non-profit page", d: "Brand it with your logo, mission, colors, and story. Each charity gets their own storefront and microsite." },
                            { n: "02", t: "Pick your tools — turn them on", d: "Auctions, donations, P2P, events, raffles, golf tournaments, fund-a-need — flip on what you need. The rest stays out of your way." },
                            { n: "03", t: "Go live & promote", d: "Email, SMS, embeddable widget, kiosk mode, live scoreboard. AI helps draft the copy. Watch the dashboard climb." },
                            { n: "04", t: "Collect & report", d: "Auto-bill winners. Smart retry on declines. Funds payout to your account. Year-end tax letters and reconciliation CSVs included." },
                        ].map((s) => (
                            <div key={s.n} className="border-t border-white/20 pt-6 flex gap-8" data-testid={`step-${s.n}`}>
                                <span className="data-num text-accent text-xl pt-1 shrink-0">{s.n}</span>
                                <div>
                                    <h3 className="font-heading text-2xl">{s.t}</h3>
                                    <p className="text-white/80 mt-2 leading-relaxed">{s.d}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* DEVICE SHOWCASE — Digital event tag on every guest's phone */}
            <DeviceShowcaseSection />

            {/* GALLERY STRIP */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24">
                <div className="grid md:grid-cols-12 gap-8 items-center">
                    <div className="md:col-span-5">
                        <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">Categories</p>
                        <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-tight">Any item. Any cause. Any format.</h2>
                        <p className="text-foreground/80 mt-6 text-lg leading-relaxed">
                            From fine art and signed memorabilia to vacation packages and one-of-a-kind experiences — your donors bid on the things they love, your cause gets the lift it needs.
                        </p>
                        <ul className="mt-8 space-y-3">
                            {["Fine art & antiques", "Luxury watches & jewelry", "Vacation & travel", "Sports & memorabilia", "Wine, spirits & dining"].map((c) => (
                                <li key={c} className="flex items-center gap-3 text-foreground font-medium"><CheckCircle size={18} weight="fill" className="text-accent" /> {c}</li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-7 grid grid-cols-2 gap-4">
                        <img src={ART_IMG} alt="" className="aspect-[4/5] object-cover w-full" />
                        <div className="space-y-4">
                            <img src={WATCH_IMG} alt="" className="aspect-square object-cover w-full" />
                            <img src={GALA_IMG} alt="" className="aspect-square object-cover w-full" />
                        </div>
                    </div>
                </div>
            </section>

            {/* PRICING — New positioning */}
            <section id="pricing" className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground">
                <div className="max-w-4xl mx-auto text-center">
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">Pricing built for non-profits</p>
                    <h2 className="font-heading text-4xl md:text-6xl mt-4 leading-tight">
                        Free to start. <em className="text-accent">Free to grow.</em>
                    </h2>
                    <p className="text-white/90 mt-6 text-lg md:text-xl leading-relaxed">
                        We don't make a dime until <em>you</em> do. No setup fees, no monthly costs, no per-seat licensing, no contracts. We get paid <em>only</em> when your donors give — and only the smallest fraction in the industry.
                    </p>
                </div>

                <div className="max-w-5xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/20" data-testid="pricing-grid">
                    {[
                        { icon: CheckCircle, h: "No setup fees", d: "Sign up, brand your page, launch your campaign — all free. Forever." },
                        { icon: CheckCircle, h: "No monthly costs", d: "No subscriptions. No 'Pro' tiers. Every feature, every module, all-in." },
                        { icon: CheckCircle, h: "We win when you win", d: "Only a tiny fraction of successful transactions — the lowest in the industry." },
                    ].map((p) => (
                        <div key={p.h} className="bg-primary p-10 text-center">
                            <p.icon size={32} className="text-accent mx-auto" weight="fill" />
                            <h3 className="font-heading text-3xl mt-6 text-white">{p.h}</h3>
                            <p className="text-white/85 mt-4 leading-relaxed">{p.d}</p>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-12">
                    <Link to="/signup-org" className="btn-accent" data-testid="pricing-cta">
                        Start fundraising free <ArrowRight size={16} weight="bold" />
                    </Link>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70 mt-6">
                        Our goal is to help you succeed. That's when we succeed.
                    </p>
                    <Link to="/compare" className="inline-flex items-center gap-2 mt-8 text-sm text-white/85 hover:text-accent transition-colors border-b border-white/30 hover:border-accent pb-1" data-testid="pricing-compare-link">
                        See how flat 2% beats Givebutter (3% or up-to-20% tip) &amp; OneCause ($5K–$25K /yr)
                        <ArrowRight size={12} weight="bold" />
                    </Link>
                </div>
            </section>

            {/* FAQ TEASER */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-background" data-testid="home-faq">
                <div className="max-w-3xl mx-auto">
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent text-center">Common questions</p>
                    <h2 className="font-heading text-4xl md:text-5xl mt-4 text-center text-foreground">
                        The short answers.
                    </h2>
                    <div className="mt-12 divide-y divide-border border-y border-border">
                        {[
                            { q: "How much does it cost?", a: "Free to start — no setup fee, no monthly subscription, no per-event license. We charge a low 2% platform fee on funds you raise, plus standard payment processing. We only earn when you do." },
                            { q: "Can donors cover the fees so we keep 100%?", a: "Yes. A 'cover the fee' toggle lets the donor absorb the platform + card fees so your organization receives the full gift. It's on by default." },
                            { q: "How fast can we get our money?", a: "Funds pay out directly to your bank via Stripe Connect on a rolling schedule — we never hold your money, and there's no middleman." },
                            { q: "Do you support silent & live auctions, galas, golf, and raffles?", a: "All of them — mobile bidding with anti-sniping, printable bid-sheet backups, QR check-in/checkout, sponsorships, fund-a-need, and license-aware raffles, all in one platform." },
                            { q: "Is it secure and PCI compliant?", a: "Yes. Payments are processed by Stripe (PCI-DSS Level 1). We never store raw card data, and access is role-based with audit trails." },
                        ].map((f, i) => (
                            <details key={i} className="group py-5" data-testid={`home-faq-${i}`}>
                                <summary className="flex items-center justify-between cursor-pointer list-none text-base md:text-lg font-medium text-foreground group-hover:text-accent transition-colors">
                                    {f.q}
                                    <span className="text-accent ml-4 shrink-0 transition-transform group-open:rotate-45 text-2xl leading-none">+</span>
                                </summary>
                                <p className="text-foreground/70 mt-3 leading-relaxed">{f.a}</p>
                            </details>
                        ))}
                    </div>
                    <div className="text-center mt-10">
                        <Link to="/faqs" className="inline-flex items-center gap-2 text-accent hover:text-amber-400 text-sm uppercase tracking-wider font-bold border-b border-accent/40 hover:border-amber-400 pb-1" data-testid="home-faq-all">
                            See all FAQs <ArrowRight size={14} weight="bold" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 text-center bg-background">
                <HandHeart size={56} weight="thin" className="mx-auto text-accent" />
                <h2 className="font-heading text-5xl md:text-7xl mt-6 leading-tight max-w-4xl mx-auto text-foreground">
                    Your next campaign starts <em className="text-accent">tonight.</em>
                </h2>
                <p className="text-foreground/80 text-xl mt-6 max-w-2xl mx-auto">
                    Free to launch. No card required. We rise when you rise.
                </p>
                <Link to="/signup-org" className="btn-accent mt-10" data-testid="final-cta">
                    Get started <ArrowRight size={16} weight="bold" />
                </Link>
            </section>

            <Footer />
        </div>
    );
}

function FeatureSection({ eyebrow, title, lead, items, testIdPrefix, inverted = false }) {
    return (
        <section className={`py-24 md:py-32 px-6 md:px-12 lg:px-24 ${inverted ? "bg-muted" : ""}`} data-testid={`section-${testIdPrefix}`}>
            <div className="max-w-3xl mb-16">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">{eyebrow}</p>
                <h2 className="font-heading text-4xl md:text-6xl mt-4 leading-tight text-foreground">{title}</h2>
                <p className="text-foreground/80 text-lg mt-6 leading-relaxed">{lead}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                {items.map((it, idx) => {
                    const Icon = it.icon;
                    return (
                        <motion.div
                            key={it.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, delay: (idx % 3) * 0.05 }}
                            className="bg-card p-8 group hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                            data-testid={`${testIdPrefix}-feature-${idx}`}
                        >
                            <Icon size={28} weight="thin" className="text-accent" />
                            <h3 className="font-heading text-2xl mt-4 text-foreground group-hover:text-white transition-colors">{it.title}</h3>
                            <p className="text-foreground/75 mt-3 leading-relaxed text-sm group-hover:text-white/85 transition-colors" dangerouslySetInnerHTML={{ __html: it.desc }} />
                        </motion.div>
                    );
                })}
            </div>
        </section>
    );
}

/* Digital event tag — shows the actual UI as it appears on guests' phones
   and the lobby kiosk on a tablet. Real screenshots from the marketing
   gallery, framed in a device chrome to feel tangible. */
function DeviceShowcaseSection() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const PHONE_1 = `${API}/api/marketing/file/27-mobile-ticket-table1-paddle103.jpg`;
    const PHONE_2 = `${API}/api/marketing/file/28-mobile-ticket-table2-paddle107.jpg`;
    const KIOSK = `${API}/api/marketing/file/30-seating-kiosk-search.jpg`;

    return (
        <section className="bg-primary text-primary-foreground py-24 md:py-32 px-6 md:px-12 lg:px-24 overflow-hidden"
                 data-testid="device-showcase-section">
            <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
                {/* Copy column */}
                <div className="md:col-span-5">
                    <p className="text-xs font-bold uppercase tracking-[0.25em] text-accent">On every device</p>
                    <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
                        Your guests arrive with their <em className="text-accent">table assignment in hand.</em>
                    </h2>
                    <p className="text-white/85 mt-6 text-lg leading-relaxed">
                        Every prepaid guest gets a personal digital event tag — paddle #, QR, and their assigned table with sponsor — bookmarkable on their phone. Volunteers scan it straight off the screen.
                    </p>

                    <ul className="mt-8 space-y-3.5">
                        {[
                            ["Mobile event tag", "QR + paddle + table + sponsor on one tap. Apple Wallet-style."],
                            ["Lobby kiosk", "Drop a tablet in the foyer — guests type their first name, table highlights instantly."],
                            ["Privacy-redacted", "Only first name + last initial appears anywhere public."],
                            ["Zero-app, zero-login", "It's just a URL. No App Store. No password. Works on every browser."],
                        ].map(([t, d]) => (
                            <li key={t} className="flex gap-3 text-white/90" data-testid={`device-bullet-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                                <CheckCircle size={20} weight="fill" className="text-accent shrink-0 mt-0.5" />
                                <span><span className="font-bold text-white">{t}.</span> <span className="text-white/75">{d}</span></span>
                            </li>
                        ))}
                    </ul>

                    <div className="flex flex-wrap gap-3 mt-10">
                        <Link to="/features#event-day"
                              className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] hover:shadow-lg transition-shadow"
                              data-testid="device-showcase-cta-features">
                            Tour live-event tools <ArrowRight size={14} weight="bold" />
                        </Link>
                        <a href="/api/marketing/gallery" target="_blank" rel="noreferrer"
                           className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 text-sm font-bold uppercase tracking-[0.18em] hover:bg-white/10 hover:border-accent transition-colors"
                           data-testid="device-showcase-cta-gallery">
                            See all screenshots
                        </a>
                    </div>
                </div>

                {/* Device mockup column */}
                <div className="md:col-span-7 relative">
                    <div className="relative h-[560px] md:h-[640px]" data-testid="device-mockup-stage">
                        {/* Gold ambient glow */}
                        <div aria-hidden className="absolute inset-0 -m-12 bg-accent/10 blur-3xl" />

                        {/* Tablet kiosk — back/right, tilted */}
                        <motion.div
                            initial={{ opacity: 0, y: 40, rotate: 8 }}
                            whileInView={{ opacity: 1, y: 0, rotate: 6 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute right-0 top-8 w-[78%] md:w-[68%] aspect-[16/10] bg-black border-[10px] md:border-[14px] border-zinc-900 rounded-2xl shadow-2xl overflow-hidden"
                            data-testid="device-mockup-tablet"
                        >
                            <img src={KIOSK} alt="Lobby kiosk · Type your first name → table highlights"
                                 className="w-full h-full object-cover object-top" loading="lazy" />
                        </motion.div>

                        {/* Phone 2 — back/left, behind primary phone */}
                        <motion.div
                            initial={{ opacity: 0, y: 60, rotate: -10 }}
                            whileInView={{ opacity: 1, y: 0, rotate: -8 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute left-2 md:left-8 bottom-4 w-[180px] md:w-[220px] aspect-[9/19] bg-zinc-900 border-[6px] md:border-[8px] border-zinc-900 rounded-[28px] md:rounded-[36px] shadow-2xl overflow-hidden"
                            data-testid="device-mockup-phone-2"
                        >
                            <img src={PHONE_2} alt="Guest's phone · Table 2 (Riverside Community Bank)"
                                 className="w-full h-full object-cover object-top" loading="lazy" />
                        </motion.div>

                        {/* Phone 1 — front/center hero phone */}
                        <motion.div
                            initial={{ opacity: 0, y: 80 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 1.0, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                            className="absolute left-1/2 -translate-x-1/2 md:left-[26%] md:translate-x-0 bottom-0 w-[230px] md:w-[280px] aspect-[9/19] bg-zinc-900 border-[8px] md:border-[10px] border-zinc-900 rounded-[34px] md:rounded-[44px] shadow-[0_30px_60px_rgba(0,0,0,0.6)] overflow-hidden ring-1 ring-accent/30"
                            data-testid="device-mockup-phone-1"
                        >
                            <img src={PHONE_1} alt="Guest's phone · Paddle #103, Table 1 (Henderson & Co.)"
                                 className="w-full h-full object-cover object-top" loading="lazy" />
                            {/* Tiny "live" indicator chip */}
                            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-14 h-3 bg-zinc-950 rounded-full" aria-hidden />
                        </motion.div>
                    </div>

                    <p className="text-center text-white/55 text-xs mt-6 max-w-md mx-auto leading-relaxed">
                        Real screenshots from the Riverside Children's Foundation demo event — 24 guests across 6 sponsored tables.
                    </p>
                </div>
            </div>
        </section>
    );
}

